import type { LitElement } from 'lit';

export type RouteParams = Record<string, string>;
type RouteHandler = (params: RouteParams) => Promise<LitElement | null>;

interface Route {
  pattern: URLPattern;
  handler: RouteHandler;
}

const routes: Route[] = [];

export function registerRoute(path: string, handler: RouteHandler): void {
  routes.push({
    pattern: new URLPattern({ pathname: path }),
    handler,
  });
}

function matchRoute(
  pathname: string,
): { route: Route; params: RouteParams } | undefined {
  for (const route of routes) {
    const result = route.pattern.exec({ pathname });
    if (result) {
      const params: RouteParams = {};
      const groups = result.pathname.groups;
      for (const [key, value] of Object.entries(groups)) {
        if (value !== undefined) params[key] = value;
      }
      return { route, params };
    }
  }
  return undefined;
}

let currentView: LitElement | null = null;
let currentPathname = '';

export const ROUTE_CHANGE_EVENT = 'app:routechange';

function scrollToTop(): void {
  window.scrollTo(0, 0);
}

function notifyRouteChange(): void {
  window.dispatchEvent(
    new CustomEvent(ROUTE_CHANGE_EVENT, {
      detail: { pathname: location.pathname },
    }),
  );
}

export async function navigate(path: string, replace = false): Promise<void> {
  if (replace) {
    history.replaceState(null, '', path);
  } else if (location.pathname !== path) {
    history.pushState(null, '', path);
  }
  await renderRoute(location.pathname);
  notifyRouteChange();
}

export function navigateBack(fallback = '/'): void {
  if (history.length > 1) {
    history.back();
  } else {
    void navigate(fallback);
  }
}

function getOutlet(): HTMLElement | null {
  const shell = document.querySelector('app-shell');
  return shell?.shadowRoot?.getElementById('outlet') ?? null;
}

async function renderRoute(pathname: string): Promise<void> {
  const outlet = getOutlet();
  if (!outlet) return;

  const match = matchRoute(pathname);
  if (!match) {
    await navigate('/', true);
    return;
  }

  const view = await match.route.handler(match.params);
  if (!view) return;

  if (currentView) {
    currentView.remove();
  }

  currentView = view;
  outlet.replaceChildren(view);

  if (currentPathname !== pathname) {
    currentPathname = pathname;
    scrollToTop();
  }
}

export function startRouter(): void {
  window.addEventListener('popstate', () => {
    void renderRoute(location.pathname).then(() => notifyRouteChange());
  });

  void renderRoute(location.pathname).then(() => notifyRouteChange());
}

export function getCurrentPath(): string {
  return location.pathname;
}
